import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateAllInfiniteCampaignReportQueries } from '../Reports/useInfiniteCampaignReportsQuery';

type BulkCancelEmailOutcome = 'Cancelled' | 'Skipped' | 'Failed';

type BulkCancelEmailReason =
	| 'AlreadyCancelled'
	| 'AlreadySent'
	| 'InProgress'
	| 'NotCancellable'
	| 'NotFound'
	| 'ProcessingFailed'
	| 'Error';

interface IBulkCancelEmailResult {
	id: string;
	outcome: BulkCancelEmailOutcome;
	reason?: BulkCancelEmailReason;
}

export interface IBulkCancelEmailsResponse {
	cancelled: number;
	skipped: number;
	failed: number;
	results: IBulkCancelEmailResult[];
}

export function useEmailCampaignsBulkCancelMutation({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (response: IBulkCancelEmailsResponse) => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({ ids, impersonationContext }: { ids: string[]; impersonationContext?: Api.IImpersonationContext }) =>
			userSession.webServiceHelper.callAsync<IBulkCancelEmailsResponse>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'email/scheduledsend/bulkCancel',
					impersonationContext,
				}),
				'POST',
				{ ids }
			),
		onError,
		onSuccess,
		// Refresh on failure too: a request that times out part-way may already have cancelled some emails.
		onSettled: () => invalidateAllInfiniteCampaignReportQueries(),
	});
}
